import * as THREE from 'three';
import {
  basicAttackDamage,
  EMOTES,
  EMOTE_COOLDOWN_MS,
  NET_INTERP_MIN_TICKS,
  NET_INTERP_TICKS,
  abilityDuration,
  type InputState,
  heroStats,
  getHeroDef,
  NEXUS_HEAL_RADIUS,
  TICK_DT,
  TICK_RATE,
  applySnapshot,
  createBrawlSimulation,
  createMatchSimulation,
  createTrainingSimulation,
  defaultRoster,
  type MatchConfig,
  distance,
  type HeroId,
  type HeroState,
  type MinionState,
  type ProjectileKind,
  type SimEvent,
  type Simulation,
  type Team,
  type WelcomeMessage,
  type WorldSnapshot,
} from '@moba/shared';
import { createArena, type Arena } from './Arena.ts';
import { CameraRig } from './CameraRig.ts';
import { FogOfWar, isVisible, visionSources } from './FogOfWar.ts';
import { HeroView } from './HeroView.ts';
import { Hud } from './Hud.ts';
import { Input, type PickTarget } from './input.ts';
import { ThornsBubble } from './ThornsBubble.ts';
import { MinionView, StructureView } from './UnitViews.ts';
import { Minimap } from './Minimap.ts';
import { Shop } from './Shop.ts';
import { SnapshotPlayback } from './netPlayback.ts';
import { NetClient, type NetStatus } from './NetClient.ts';
import { Vfx } from './Vfx.ts';
import { disposeGroup, makeGiantSword, makeTriforce } from './TriforceFx.ts';
import { Sfx } from './Sfx.ts';
import { TouchControls, isTouchDevice } from './TouchControls.ts';
import {
  instantiateCharacter,
  loadCharacters,
  loadManifest,
  loadMinionModels,
  loadPropModels,
  type CharacterAsset,
  type MinionAssetKey,
} from './assets.ts';

import { t, matchLabelT } from './i18n.ts';
/** Partida offline: contra bots (`match`) o entrenamiento con muñecos (`training`). */
export type OfflineKind = 'match' | 'training';

/** Ajustes gráficos (Configuración → Gráficos). */
export interface GraphicsSettings {
  /** high: mapa 2048 cada frame; low: 1024 a frames alternos; off: sin sombras */
  shadows: 'high' | 'low' | 'off';
  /** 0 = sin límite */
  fpsCap: 0 | 30 | 60;
  /** baja la resolución interna si los FPS caen y la recupera si sobran */
  dynamicResolution: boolean;
}

/** FPS del render con el menú (overlay) abierto: el mundo apenas se ve detrás del desenfoque. */
const MENU_FPS = 8;
/** FPS con la partida offline en pausa. */
const PAUSED_FPS = 15;
/** Margen (unidades del mundo) para dibujar/animar unidades un poco antes de que entren en pantalla. */
const VIEW_MARGIN = 5;

export class Game {
  sim: Simulation;
  private assets = new Map<HeroId, CharacterAsset>();

  private readonly renderer: THREE.WebGLRenderer;
  private readonly scene = new THREE.Scene();
  private readonly cameraRig: CameraRig;
  private readonly input: Input;
  /** controles táctiles (móvil en horizontal); sólo en pantallas táctiles */
  private touch?: TouchControls;
  /** guía de apuntado táctil en el suelo: franja desde el héroe + círculo en el destino */
  private aimGuide?: { group: THREE.Group; strip: THREE.Mesh; ring: THREE.Mesh };
  /** la pausa la abre `main` (menú); los controles táctiles la piden con este callback */
  onPauseRequest?: () => void;
  /** botones táctiles de chat y emojis (los gestiona main.ts) */
  onTouchChat?: () => void;
  onTouchEmote?: () => void;
  private readonly hud = new Hud();
  private readonly views = new Map<number, HeroView>();
  private readonly structureViews = new Map<number, StructureView>();
  private readonly minionViews = new Map<number, MinionView>();
  private minionModels = new Map<MinionAssetKey, THREE.Group>();
  private readonly heroHeights = new Map<number, number>();
  private minimap?: Minimap;
  private shop?: Shop;
  private online = false;

  private arena!: Arena;
  private vfx!: Vfx;
  /** efectos de sonido sintetizados (M silencia) */
  private readonly sfx = new Sfx();
  private fog?: FogOfWar;
  /** focos de visión aliados del último frame (null = sin niebla); también filtran el sonido */
  private fogSources: ReturnType<typeof visionSources> | null = null;
  private overlayEl: HTMLElement | null = null;
  private net?: NetClient;
  private welcome?: WelcomeMessage;
  private matchEnded = false;
  /** online: búfer de interpolación de snapshots */
  private readonly playback = new SnapshotPlayback<WorldSnapshot>(TICK_RATE, NET_INTERP_TICKS, 15, NET_INTERP_MIN_TICKS);
  /** menú de pausa abierto (offline: la simulación se detiene) */
  paused = false;
  /** cámara bloqueada al héroe; Y la libera para pasear con el cursor en los bordes */
  private cameraLocked = true;
  private mouseSensitivity = 1;
  private running = false;
  private accumulator = 0;
  private lastTime = 0;
  private fps = 60;
  private fpsSamples = 0;
  private fpsTime = 0;
  private lastMoveSerial = 0;
  private lastPlayerHp = 0;
  /** posición predicha del héroe propio (online) */
  private predicted?: { x: number; z: number };
  private graphics: GraphicsSettings = { shadows: 'high', fpsCap: 0, dynamicResolution: true };
  /** momento del último frame dibujado (límite de FPS / menú) */
  private lastFrameAt = 0;
  private frameCount = 0;
  /** resolución interna: máximo del dispositivo y escala dinámica actual (0.6..1) */
  private basePixelRatio = 1;
  private resolutionScale = 1;
  private dynResTime = 0;
  private dynResFrames = 0;
  private dynResCalm = 0;
  private readonly ndc = new THREE.Vector3();

  constructor(private readonly canvas: HTMLCanvasElement) {
    // mundo provisional (precalienta shaders); cada partida lo rehace con su configuración
    this.sim = createBrawlSimulation();

    this.renderer = new THREE.WebGLRenderer({
      canvas,
      antialias: true,
      powerPreference: 'high-performance',
    });
    this.basePixelRatio = Math.min(window.devicePixelRatio, 2);
    this.renderer.setPixelRatio(this.basePixelRatio);
    this.renderer.setSize(window.innerWidth, window.innerHeight);
    this.renderer.outputColorSpace = THREE.SRGBColorSpace;
    this.renderer.toneMapping = THREE.ACESFilmicToneMapping;
    this.renderer.toneMappingExposure = 1.12;
    this.renderer.shadowMap.enabled = true;
    this.renderer.shadowMap.type = THREE.PCFShadowMap;

    this.cameraRig = new CameraRig(window.innerWidth / window.innerHeight);
    this.input = new Input(canvas);
    if (isTouchDevice()) {
      document.body.classList.add('touch');
      this.input.touchMode = true;
      // en el móvil se dibuja a menos resolución: más FPS y menos batería
      this.basePixelRatio = Math.min(window.devicePixelRatio, 1.5);
      this.renderer.setPixelRatio(this.basePixelRatio);
      // pantalla pequeña: cámara algo más cerca para que se vean los personajes
      this.cameraRig.viewHalfHeight = 8.5;
      this.cameraRig.resize(window.innerWidth, window.innerHeight);
      this.touch = new TouchControls({
        input: this.input,
        camera: () => this.cameraRig.camera,
        sim: () => this.sim,
        showAim: (from, to, radius) => this.showAimGuide(from, to, radius),
        onPause: () => this.onPauseRequest?.(),
        onShop: () => {
          this.hud.setHeroBoardOpen(false);
          this.shop?.toggle();
        },
        onBoard: () => {
          this.shop?.setOpen(false);
          this.hud.toggleHeroBoard();
        },
        onChat: () => this.onTouchChat?.(),
        onEmote: () => this.onTouchEmote?.(),
        chatAvailable: () => !(document.getElementById('game-chat')?.hidden ?? true),
      });
    }
    // online, cada tecla/clic sale al servidor en el acto, sin esperar al próximo tick
    this.input.onCommand = () => this.sendInputNow();
    this.hud.onRankUp = (slot) => this.input.requestLevelUp(slot);
  }

  async init(onProgress?: (fraction: number, label: string) => void): Promise<void> {
    this.arena = createArena(this.scene);
    this.fog = new FogOfWar(this.scene);
    this.overlayEl = document.getElementById('overlay');
    // niebla de guerra también en el audio: lo que no ve tu equipo no se oye
    this.sfx.audibleAt = (x, z) => !this.fogSources || isVisible(this.fogSources, x, z, 3);

    const damageLayer = document.getElementById('damage-layer');
    if (!damageLayer) throw new Error('Falta #damage-layer');
    this.vfx = new Vfx(this.scene, damageLayer);
    // la Trifuerza (R de Link) sigue la posición interpolada de su objetivo
    this.vfx.unitPosition = (id) => {
      const view = this.views.get(id);
      return view ? { x: view.root.position.x, z: view.root.position.z } : undefined;
    };
    this.vfx.unitVisible = (id) => this.views.get(id)?.root.visible ?? false;
    this.vfx.onScreen = (x, z) => this.inView(x, z);

    const manifest = await loadManifest();
    this.assets = await loadCharacters(manifest, onProgress, this.touch ? 1024 : 0);
    this.minionModels = await loadMinionModels();
    await loadPropModels();
    this.buildWorldViews();

    this.warmUpShaders();
    this.minimap = new Minimap((x, z) => this.input.setMoveTarget(x, z));
    this.shop = new Shop(
      (itemId) => this.input.requestBuy(itemId),
      (slot) => this.input.requestSell(slot),
    );
    document.getElementById('shop-btn')?.addEventListener('click', () => this.shop?.toggle());

    window.addEventListener('resize', this.onResize);
    window.addEventListener('wheel', this.onWheel, { passive: false });
    window.addEventListener('keydown', this.onKeyDown);
    window.addEventListener('keyup', this.onKeyUp);
    window.addEventListener('blur', () => { this.rangeKeyHeld = false; });
    // pestaña oculta: el navegador ya para el render; además se suspende el audio
    document.addEventListener('visibilitychange', () => this.sfx.setPageHidden(document.hidden));
    this.arena.setShadowQuality(this.graphics.shadows);

    this.hud.setNetStatus(null);

    this.lastTime = performance.now();
    requestAnimationFrame(this.frame);
  }

  /** Modelos cargados de cada héroe (galería de personajes). */
  /** Sonido asignado a un clip (galería de personajes). */
  previewClipSound(heroId: HeroId, clipKey: string): void {
    this.sfx.previewClip(heroId, clipKey);
  }

  get characterAssets(): Map<HeroId, CharacterAsset> {
    return this.assets;
  }

  /** Vistas de héroes y estructuras para la simulación actual. */
  private buildWorldViews(): void {
    for (const hero of this.sim.heroes) {
      const asset = this.assets.get(hero.heroId);
      if (!asset) continue;
      const view = new HeroView(hero, asset);
      // golpe contra el suelo (E de Bowser): tiembla la cámara aunque no alcance a nadie
      view.onSlam = (x, z) => this.vfx.addShake(0.7, 1.5, x, z, hero.id === this.sim.player?.id);
      this.views.set(hero.id, view);
      this.heroHeights.set(hero.id, asset.manifest.targetHeight);
      this.scene.add(view.root);
    }
    for (const structure of this.sim.structures) {
      const asset = this.assets.get(structure.model);
      if (!asset) continue;
      const view = new StructureView(structure, asset);
      this.structureViews.set(structure.id, view);
      this.scene.add(view.root);
    }
    useSkinnedShadowDepth(this.scene);
  }

  /** Cambia de partida: retira las vistas de la anterior y crea las de `sim`. */
  private rebuildWorld(sim: Simulation): void {
    for (const view of this.views.values()) view.root.removeFromParent();
    for (const view of this.structureViews.values()) view.root.removeFromParent();
    for (const view of this.minionViews.values()) view.dispose();
    this.views.clear();
    this.structureViews.clear();
    this.minionViews.clear();
    this.heroHeights.clear();
    this.sim = sim;
    this.vfx.syncProjectiles([], 1);
    // entrenamiento: las tres líneas quedan como decorado
    this.arena.setActiveLanes(sim.laneEnabled ? sim.activeLanes : [0, 1, 2]);
    // un escenario por modo: entrenamiento, ARAM (una línea) o 3v3 (tres líneas)
    this.arena.setTheme(!sim.match.enabled ? 'training' : sim.activeLanes.length === 1 ? 'sky' : 'kingdom');
    this.buildWorldViews();
    this.sfx.reset();
    this.lastPlayerHp = this.sim.player?.hp ?? 0;
  }

  /**
   * Retratos (data URL) de cada héroe para la pantalla de selección, renderizados
   * desde su propio modelo en pose idle con un renderer pequeño aparte.
   */
  renderPortraits(size = 160): Map<HeroId, string> {
    const out = new Map<HeroId, string>();
    const renderer = new THREE.WebGLRenderer({ antialias: true, alpha: true, preserveDrawingBuffer: true });
    renderer.setSize(size, size);
    renderer.outputColorSpace = THREE.SRGBColorSpace;
    const scene = new THREE.Scene();
    scene.add(new THREE.HemisphereLight(0xdfeeff, 0x1a2230, 1.6));
    const key = new THREE.DirectionalLight(0xffffff, 2.2);
    key.position.set(2, 4, 5);
    scene.add(key);
    const camera = new THREE.PerspectiveCamera(30, 1, 0.05, 50);
    for (const [heroId, asset] of this.assets) {
      const instance = instantiateCharacter(asset);
      const idle = THREE.AnimationClip.findByName(instance.clips, asset.manifest.clips.idle);
      if (idle) {
        const mixer = new THREE.AnimationMixer(instance.model);
        mixer.clipAction(idle).play();
        mixer.update(0.3);
      }
      scene.add(instance.root);
      // busto: encuadra la mitad superior del personaje, de frente
      const h = asset.manifest.targetHeight;
      camera.position.set(0, h * 0.72, h * 1.45);
      camera.lookAt(0, h * 0.66, 0);
      renderer.render(scene, camera);
      out.set(heroId, renderer.domElement.toDataURL('image/png'));
      instance.root.removeFromParent();
    }
    renderer.dispose();
    renderer.forceContextLoss();
    this.hud.setPortraits(out);
    return out;
  }

  /** Partida offline: contra bots con la configuración elegida, o entrenamiento. */
  startOffline(kind: OfflineKind, config: MatchConfig, playerHero: HeroId): void {
    this.leaveOnline();
    const sim =
      kind === 'training'
        ? createTrainingSimulation(playerHero)
        : createMatchSimulation({ config, roster: defaultRoster(config, playerHero) }, { team: 'blue', slot: 0 });
    this.rebuildWorld(sim);
    this.hud.setModeLabel(kind === 'training' ? t('ENTRENAMIENTO') : matchLabelT(config).toUpperCase());
    this.hud.setNetStatus(null);
    this.beginPlay();
    this.running = true;
  }

  /**
   * Partida online: crea la misma simulación que el servidor (configuración y
   * alineación de la bienvenida) y a partir de ahí reproduce sus snapshots.
   */
  startOnline(net: NetClient, welcome: WelcomeMessage): void {
    this.net = net;
    this.online = true;
    this.welcome = welcome;
    this.predicted = undefined;
    const sim = createMatchSimulation(welcome.setup, null);
    for (const hero of sim.heroes) hero.isPlayer = hero.id === welcome.heroId;
    this.rebuildWorld(sim);
    this.resetNetPlayback();
    this.hud.setModeLabel(`${matchLabelT(welcome.setup.config).toUpperCase()} · ONLINE`);
    this.hud.setNetStatus('ONLINE', 'connected');
    this.beginPlay();
    this.running = true;
  }

  /** Snapshot del servidor (sólo se reproduce durante una partida online). */
  pushSnapshot(snapshot: WorldSnapshot): void {
    this.onSnapshot(snapshot);
  }

  /** Online: la partida acabó y la sala vuelve al lobby. */
  stopOnline(): void {
    this.running = false;
    this.welcome = undefined;
    this.matchEnded = false;
    this.input.enabled = false;
    this.input.clearMoveTarget();
    this.input.clearTarget();
    this.input.clearPressed();
    this.resetNetPlayback();
    this.hud.hideMatchEnd();
  }

  /** Sale de la sala online (al volver a jugar offline). */
  private leaveOnline(): void {
    if (!this.online) return;
    this.net?.leave();
    this.net = undefined;
    this.online = false;
    this.welcome = undefined;
    this.resetNetPlayback();
  }

  get netStatusHandler(): (status: NetStatus, detail?: string) => void {
    return (status, detail) => this.onNetStatus(status, detail);
  }

  private beginPlay(): void {
    this.input.sample();
    this.input.enabled = true;
    this.lastPlayerHp = this.sim.player?.hp ?? 0;
    this.accumulator = 0;
    this.lastTime = performance.now();
    this.matchEnded = false;
    this.cameraLocked = true;
    this.cameraRig.setLocked(true);
    this.hud.setCameraLocked(true);
    this.hud.setHeroBoardOpen(false);

    this.paused = false;
    this.hud.hideMatchEnd();
  }

  private readonly frame = (now: number): void => {
    requestAnimationFrame(this.frame);

    // cuántos frames se dibujan: pocos con el menú delante o en pausa, y el
    // límite de FPS de Configuración; el tiempo saltado se acumula en `dt`
    const menuOpen = !this.overlayEl || !this.overlayEl.classList.contains('hidden');
    const fpsLimit = menuOpen ? MENU_FPS : this.paused && !this.online ? PAUSED_FPS : this.graphics.fpsCap;
    if (fpsLimit > 0 && now - this.lastFrameAt < 1000 / fpsLimit - 2) return;
    this.lastFrameAt = now;
    this.frameCount++;

    const dt = Math.min(0.1, Math.max(0, (now - this.lastTime) / 1000));
    this.lastTime = now;

    this.fpsTime += dt;
    this.fpsSamples++;
    if (this.fpsTime >= 0.35) {
      this.fps = this.fpsSamples / this.fpsTime;
      this.fpsTime = 0;
      this.fpsSamples = 0;
    }

    const player = this.sim.player;
    if (!player) return;

    this.input.updateAim(this.cameraRig.camera);
    this.input.setPlayerPosition(player.pos.x, player.pos.z);
    // al morir el objetivo, el autoataque pasa a otro enemigo dentro de alcance + 3
    this.input.autoAcquireRange =
      getHeroDef(player.heroId).attacks[0].range * heroStats(player.heroId, player.level, player.items).attackRange + 3;
    this.vfx.setListener(player.pos.x, player.pos.z, player.id);
    this.sfx.setListener(player.pos.x, player.pos.z, player.id, player.team, this.cameraRig.camera);
    this.input.setEnemies(this.pickTargets(player.team));
    this.input.setSelectables(this.pickTargets(null));
    if (!player.alive) {
      this.input.clearMoveTarget();
      this.input.clearTarget();
    }

    if (this.input.moveTargetSerial !== this.lastMoveSerial) {
      this.lastMoveSerial = this.input.moveTargetSerial;
      if (this.input.moveTarget.active) {
        this.vfx.playMoveMarker(this.input.moveTarget.x, this.input.moveTarget.z);
      }
    }

    if (this.running) {
      this.accumulator += dt;
      let steps = 0;
      while (this.accumulator >= TICK_DT && steps < 5) {
        if (this.online) {
          if (!this.matchEnded) {
            const input = this.input.sample();
            this.predictOwnCast(input);
            this.net?.sendInput(input, true);
          } else {
            this.input.sample();
          }
        } else {
          const input = this.input.sample();
          const events = this.sim.step(new Map([[player.id, input]]));
          for (const event of events) this.handleEvent(event);
        }
        this.accumulator -= TICK_DT;
        steps++;
      }
      if (steps === 5) this.accumulator = 0;
    }

    const alpha = this.online
      ? this.updateNetPlayback(dt)
      : this.running
        ? Math.min(1, this.accumulator / TICK_DT)
        : 1;

    this.sfx.update(this.sim);
    this.touch?.update(
      player,
      this.running && !this.paused && !this.matchEnded && Boolean(this.overlayEl?.classList.contains('hidden')),
    );
    this.hud.setPing(this.online && this.net ? this.net.ping : null);
    if (this.online) this.updateLocalPrediction(player, alpha, dt);
    for (const view of this.views.values()) {
      view.setTargeted(view.hero.id === this.input.targetId);
      // sólo se anima lo que se ve (visibilidad del frame anterior: el margen la cubre)
      view.update(dt, alpha, view.root.visible || view.hero.id === player.id);
    }
    this.syncMinionViews();
    const lastHit = player.alive ? basicAttackDamage(player.heroId, player.level, player.items) : 0;
    for (const view of this.minionViews.values()) {
      view.setTargeted(view.minion.id === this.input.targetId);
      view.lastHitDamage = view.minion.team !== player.team ? lastHit : 0;
      view.update(dt, alpha);
    }
    for (const view of this.structureViews.values()) {
      view.setTargeted(view.structure.id === this.input.targetId);
      view.update(dt, player);
    }
    this.updateFog(player);
    this.updateVisibility(player);
    this.updateAudio(player);
    this.vfx.syncProjectiles(this.sim.projectiles, alpha);
    this.vfx.update(dt, this.cameraRig.camera, window.innerWidth, window.innerHeight);
    this.arena.update(dt, this.cameraRig.focusPoint);

    const playerView = this.views.get(player.id);
    const position = playerView ? playerView.root.position : player.pos;
    this.updateHealingFx(player, position);
    this.cameraRig.shake = Math.max(this.cameraRig.shake, this.vfx.shake);
    const peek = this.minimap?.peek;
    if (peek) this.cameraRig.update(dt, peek, peek);
    else {
      if (!this.touch && !this.cameraLocked && this.running && !this.paused && !this.matchEnded && !this.shop?.isOpen) {
        this.updateEdgePan(dt);
      }
      // táctil: sin cursor, la cámara no se adelanta hacia el punto de mira
      const lead = this.touch ? { x: position.x, z: position.z } : this.input.aim;
      this.cameraRig.update(dt, { x: position.x, z: position.z }, lead);
    }
    this.minimap?.update(this.sim, this.cameraRig.camera);
    this.shop?.update(this.sim, player);

    this.hud.update(
      this.sim,
      this.input.aim,
      this.fps,
      this.input.screenX,
      this.input.screenY,
      this.input.selectedId,
    );

    this.updateRangeIndicator(player);
    this.updateDynamicResolution(dt);
    // sombras bajas: el mapa de sombras se redibuja en frames alternos
    if (!this.renderer.shadowMap.autoUpdate && this.frameCount % 2 === 0) this.renderer.shadowMap.needsUpdate = true;
    this.renderer.render(this.scene, this.cameraRig.camera);
  };

  /**
   * Niebla de guerra: oscurece el suelo fuera de la visión aliada y oculta los
   * héroes/minions enemigos que no están a la vista. En training no se aplica.
   */
  private updateFog(player: HeroState): void {
    if (!this.fog) return;
    if (!this.sim.match.enabled) {
      this.fog.update([]);
      this.fogSources = null;
      return;
    }
    const sources = visionSources(this.sim, player.team);
    this.fogSources = sources;
    this.fog.update(sources);
    // qué enemigos se ven lo decide `updateVisibility` (niebla + cámara)
  }

  /**
   * Música según el momento: tema de menú con el overlay abierto, tema de
   * partida (más intenso si hay héroes enemigos visibles cerca o acabas de
   * recibir daño) y silencio musical al terminar, dejando la fanfarria.
   */
  private updateAudio(player: HeroState): void {
    const inMenu = !this.overlayEl || !this.overlayEl.classList.contains('hidden');
    if (inMenu) {
      this.sfx.setMusic('menu');
      this.sfx.setAmbient(false);
      return;
    }
    this.sfx.setAmbient(true);
    if (this.matchEnded) {
      this.sfx.setMusic('none');
      return;
    }
    let combat = player.alive && this.sim.tick - player.lastDamagedAt < TICK_RATE * 4;
    if (!combat && player.alive) {
      for (const hero of this.sim.heroes) {
        if (!hero.alive || hero.hidden || hero.team === player.team || hero.isDummy) continue;
        if (distance(hero.pos, player.pos) > 16) continue;
        if (this.fogSources && !isVisible(this.fogSources, hero.pos.x, hero.pos.z, hero.radius)) continue;
        combat = true;
        break;
      }
    }
    this.sfx.setMusic('match', this.paused ? 0 : combat ? 1 : 0.12);
  }

  /** Volumen, silencio y música (Configuración y el botón de sonido del menú). */
  get audio(): Sfx {
    return this.sfx;
  }

  /** Guía de apuntado de los botones táctiles (null = ocultarla). */
  private showAimGuide(from: { x: number; z: number } | null, to?: { x: number; z: number }, radius = 0.6): void {
    if (!from || !to) {
      if (this.aimGuide) this.aimGuide.group.visible = false;
      return;
    }
    if (!this.aimGuide) {
      const material = new THREE.MeshBasicMaterial({ color: 0x8fd0ff, transparent: true, opacity: 0.38, depthWrite: false, side: THREE.DoubleSide });
      const strip = new THREE.Mesh(new THREE.PlaneGeometry(1, 1), material);
      strip.rotation.x = -Math.PI / 2;
      const ring = new THREE.Mesh(new THREE.RingGeometry(0.75, 1, 40), material.clone());
      (ring.material as THREE.MeshBasicMaterial).opacity = 0.75;
      ring.rotation.x = -Math.PI / 2;
      const group = new THREE.Group();
      group.add(strip, ring);
      group.renderOrder = 5;
      this.scene.add(group);
      this.aimGuide = { group, strip, ring };
    }
    const { group, strip, ring } = this.aimGuide;
    const dx = to.x - from.x;
    const dz = to.z - from.z;
    const length = Math.max(0.1, Math.hypot(dx, dz));
    group.visible = true;
    strip.position.set((from.x + to.x) / 2, 0.06, (from.z + to.z) / 2);
    strip.scale.set(0.55, length, 1);
    strip.rotation.set(-Math.PI / 2, 0, Math.atan2(dx, dz) + Math.PI);
    ring.position.set(to.x, 0.07, to.z);
    ring.scale.setScalar(radius);
  }

  /** Con la cámara libre (Y), acercar el cursor a un borde panea la vista. */
  private updateEdgePan(dt: number): void {
    const margin = 24;
    const width = window.innerWidth;
    const height = window.innerHeight;
    const x = this.input.screenX;
    const y = this.input.screenY;
    let px = 0;
    let py = 0;
    if (x < margin) px = -1 + x / margin;
    else if (x > width - margin) px = 1 - (width - x) / margin;
    if (y < margin) py = 1 - y / margin;
    else if (y > height - margin) py = -1 + (height - y) / margin;
    if (px === 0 && py === 0) return;
    const step = 22 * this.mouseSensitivity * dt;
    this.cameraRig.panScreen(px * step, py * step);
  }

  /**
   * Manteniendo A se ve el alcance del ataque básico: del héroe seleccionado
   * (clic izquierdo sobre él) o, si no hay ninguno, del propio.
   */
  private updateRangeIndicator(player: HeroState): void {
    const selected = this.sim.heroes.find((hero) => hero.id === this.input.selectedId && hero.alive);
    const hero = selected ?? player;
    const show = this.rangeKeyHeld && this.running && hero.alive;
    if (!show) {
      if (this.rangeIndicator) this.rangeIndicator.group.visible = false;
      return;
    }
    const radius = getHeroDef(hero.heroId).attacks[0].range * heroStats(hero.heroId, hero.level, hero.items).attackRange;
    let indicator = this.rangeIndicator;
    if (!indicator || Math.abs(indicator.radius - radius) > 1e-3) {
      if (indicator) {
        indicator.group.removeFromParent();
        indicator.group.traverse((obj) => {
          if (obj instanceof THREE.Mesh) {
            obj.geometry.dispose();
            (obj.material as THREE.Material).dispose();
          }
        });
      }
      const group = new THREE.Group();
      const flat = (geometry: THREE.BufferGeometry, opacity: number) => {
        const mesh = new THREE.Mesh(
          geometry,
          new THREE.MeshBasicMaterial({ color: 0xffffff, transparent: true, opacity, depthWrite: false, side: THREE.DoubleSide, forceSinglePass: true }),
        );
        mesh.rotation.x = -Math.PI / 2;
        mesh.renderOrder = 3;
        group.add(mesh);
        return mesh;
      };
      flat(new THREE.CircleGeometry(radius, 96), 0.08);
      flat(new THREE.RingGeometry(Math.max(0, radius - 0.1), radius, 96), 0.75);
      group.position.y = 0.04;
      this.scene.add(group);
      indicator = this.rangeIndicator = { group, radius };
    }
    const color = hero.team === player.team ? 0x6fc3ff : 0xff6b6b;
    indicator.group.traverse((obj) => {
      if (obj instanceof THREE.Mesh) (obj.material as THREE.MeshBasicMaterial).color.setHex(color);
    });
    indicator.group.position.x = hero.pos.x;
    indicator.group.position.z = hero.pos.z;
    indicator.group.visible = true;
  }

  /**
   * Detecta cuándo sube la vida del jugador y emite las cruces verdes, pero
   * sólo si está curándose en su base (cerca del nexo aliado). Ignora el salto
   * de vida al reaparecer o cualquier cambio demasiado grande para ser curación.
   */
  private updateHealingFx(player: HeroState, position: { x: number; z: number }): void {
    const gained = player.hp - this.lastPlayerHp;
    this.lastPlayerHp = player.hp;
    if (!player.alive || gained <= 0.001 || gained > player.maxHp * 0.2) return;
    const nexus = this.sim.structures.find(
      (structure) => structure.kind === 'nexus' && structure.team === player.team && structure.alive,
    );
    if (!nexus || distance(nexus.pos, player.pos) > NEXUS_HEAL_RADIUS) return;
    this.vfx.showHealing(position.x, position.z);
  }

  private handleEvent(event: SimEvent): void {
    this.vfx.play(event);
    this.sfx.play(event, this.sim);

    if (event.type === 'death') {
      const victim = this.sim.heroes.find((hero) => hero.id === event.heroId);
      const killer =
        event.killerId === undefined
          ? undefined
          : this.sim.heroes.find((hero) => hero.id === event.killerId);
      if (victim) this.hud.killFeed(killer, victim);
    }

    if (event.type === 'levelUp' && event.heroId === this.sim.player?.id) {
      this.vfx.announce(t('¡Nivel {level}!', { level: event.level }), '#d6b3ff');
    }

    if (event.type === 'gold' && event.heroId === this.sim.player?.id) {
      this.vfx.goldGain(event.x, event.z, event.amount);
    }

    if (event.type === 'structureDestroyed') {
      const player = this.sim.player;
      const allied = player ? event.team === player.team : event.team === 'blue';
      const nexus = event.kind === 'nexus';
      this.vfx.announce(
        allied
          ? t(nexus ? '¡Nexo aliado destruido!' : '¡Torre aliada destruida!')
          : t(nexus ? '¡Nexo enemigo destruido!' : '¡Torre enemiga destruida!'),
        allied ? '#ff7a6b' : '#7ce6c0',
      );
    }

    if (event.type === 'matchEnd') {
      this.finishMatch(event.winner, event.blue, event.red);
    }
  }

  /**
   * Compila de antemano los shaders de todo lo que aparecerá en la partida
   * (minions de ambos equipos incluidos), para evitar tirones la primera vez.
   */
  private warmUpShaders(): void {
    // Un frame real (bajo la pantalla de carga) con un minion de cada equipo y un
    // proyectil de cada tipo: compila shaders y sube texturas antes de jugar.
    this.cameraRig.update(0, { x: 0, z: 0 }, { x: 0, z: 1 });
    const views: MinionView[] = [];
    for (const team of ['blue', 'red'] as Team[]) {
      const model = this.minionModels.get(`koopa-${team}`);
      if (!model) continue;
      const fake: MinionState = {
        id: -1, kind: 'koopa', team, lane: 1, waypoint: 0, pos: { x: team === 'blue' ? -1 : 1, z: 0 }, prevPos: { x: 0, z: 0 },
        vel: { x: 0, z: 0 }, facing: 0, radius: 0.42, speed: 0, hp: 1, maxHp: 2, alive: true,
        cooldown: 0, targetId: 0, attackTicks: 99,
      };
      const view = new MinionView(fake, model);
      view.update(0, 1);
      this.scene.add(view.root);
      views.push(view);
      // y uno muriendo: compila la variante transparente (sus materiales quedan en el pool)
      const dying = new MinionView({ ...fake, id: -2, pos: { x: fake.pos.x * 2, z: 0 } }, model);
      dying.die();
      dying.update(1.2, 1);
      this.scene.add(dying.root);
      views.push(dying);
    }
    const kinds: ProjectileKind[] = ['orb', 'arrow', 'missile', 'needle', 'fire', 'bomb', 'blackbomb', 'blaster', 'tower'];
    const fakeProjectiles = kinds.map((kind, i) => ({
      id: -100 - i, ownerId: 0, team: 'blue' as Team, kind, pos: { x: i * 0.3, z: 0.5 }, prevPos: { x: i * 0.3, z: 0.5 },
      vel: { x: 0, z: 0 }, radius: 0.3, damage: 0, knockback: 0, hitstun: 0, ttl: 1, alive: true, reflected: false,
      pierce: 0, hitIds: [], explodeRadius: 0, explodeDamage: 0, explodeKnockback: 0, explodeHitstun: 0,
      explodeOnExpire: false, explodeOnHit: false, homingId: 0,
    }));
    this.vfx.syncProjectiles(fakeProjectiles, 1);
    // Trifuerza y espada gigante (R de Link): materiales metálicos transparentes
    const triforce = makeTriforce(1);
    const giantSword = makeGiantSword(1);
    triforce.position.set(-2, 1, 0);
    giantSword.position.set(2, 0, 0);
    this.scene.add(triforce, giantSword);
    // burbuja de espinas (W de Bowser): shader propio
    const thorns = new ThornsBubble(1, 2);
    thorns.group.visible = true;
    this.scene.add(thorns.group);
    // golpe contra el suelo (E de Bowser): las mismas variantes de material que la onda,
    // la marca, el polvo y las piedras, para que el primer impacto no dé un tirón
    const slamBits = new THREE.Group();
    const basic = (side: THREE.Side, forceSinglePass: boolean) =>
      new THREE.MeshBasicMaterial({ color: 0xffffff, transparent: true, opacity: 0.5, depthWrite: false, side, forceSinglePass });
    slamBits.add(
      new THREE.Mesh(new THREE.RingGeometry(0.8, 1, 16), basic(THREE.DoubleSide, true)),
      new THREE.Mesh(new THREE.CircleGeometry(1, 16), basic(THREE.FrontSide, true)),
      new THREE.Mesh(new THREE.IcosahedronGeometry(0.3, 1), basic(THREE.FrontSide, false)),
      new THREE.Mesh(new THREE.DodecahedronGeometry(0.1, 0), new THREE.MeshBasicMaterial({ color: 0x5b4632, transparent: true, opacity: 1 })),
    );
    slamBits.position.set(0, 0.1, -2);
    this.scene.add(slamBits);
    this.renderer.compile(this.scene, this.cameraRig.camera);
    this.renderer.render(this.scene, this.cameraRig.camera);
    this.vfx.syncProjectiles([], 1);
    for (const view of views) view.dispose();
    thorns.group.removeFromParent();
    // oculto pero vivo: si se desechara, three.js soltaría los programas compilados
    slamBits.visible = false;
    triforce.removeFromParent();
    giantSword.removeFromParent();
    disposeGroup(triforce);
    disposeGroup(giantSword);
    thorns.dispose();
  }

  /** Héroes, minions y estructuras enemigas que se pueden fijar con clic izquierdo. */
  /** `team`: enemigos de ese equipo; `null`: todas las unidades (para inspeccionar). */
  private pickTargets(team: Team | null): PickTarget[] {
    const targets: PickTarget[] = [];
    for (const hero of this.sim.heroes) {
      if (team !== null && hero.team === team) continue;
      targets.push({ id: hero.id, pos: hero.pos, radius: hero.radius, alive: hero.alive && !hero.hidden, height: this.heroHeights.get(hero.id) ?? 2, pickPadding: 1.5 });
    }
    for (const minion of this.sim.minions) {
      if (team !== null && minion.team === team) continue;
      targets.push({ id: minion.id, pos: minion.pos, radius: minion.radius, alive: minion.alive && !minion.hidden, height: 1.4, pickPadding: 1.2 });
    }
    for (const structure of this.sim.structures) {
      if (team !== null && (structure.team === team || !this.sim.isStructureVulnerable(structure))) continue;
      const view = this.structureViews.get(structure.id);
      targets.push({ id: structure.id, pos: structure.pos, radius: structure.radius, alive: structure.alive, height: view?.height ?? 3, pickPadding: 0.8 });
    }
    return targets;
  }

  /** Crea vistas para minions nuevos y retira las de minions que ya no existen. */
  private syncMinionViews(): void {
    const alive = new Set<number>();
    for (const minion of this.sim.minions) {
      if (!minion.alive) continue;
      alive.add(minion.id);
      if (this.minionViews.has(minion.id)) continue;
      const model = this.minionModels.get(`${minion.kind}-${minion.team}`);
      if (!model) continue;
      const view = new MinionView(minion, model);
      this.minionViews.set(minion.id, view);
      this.scene.add(view.root);
    }
    // los muertos se quedan un rato: caen y se desvanecen en el suelo
    for (const [id, view] of this.minionViews) {
      if (alive.has(id)) continue;
      if (!view.dying) view.die();
      if (!view.finished) continue;
      view.dispose();
      this.minionViews.delete(id);
    }
  }

  /**
   * Predicción del movimiento propio (online): con clic derecho en el suelo el
   * héroe echa a andar en pantalla al instante hacia el destino, sin esperar al
   * servidor (~RTT + búfer de interpolación). El punto predicho nunca se adelanta
   * más de lo que el servidor puede recorrer en ese retraso y, sin orden de
   * movimiento, vuelve suavemente a la posición del servidor (que manda).
   */
  private updateLocalPrediction(player: HeroState, alpha: number, dt: number): void {
    const view = this.views.get(player.id);
    if (!view) return;
    const rx = player.prevPos.x + (player.pos.x - player.prevPos.x) * alpha;
    const rz = player.prevPos.z + (player.pos.z - player.prevPos.z) * alpha;
    const pred = this.predicted ?? { x: rx, z: rz };
    this.predicted = pred;
    const target = this.input.moveTarget;
    const speed = heroStats(player.heroId, player.level, player.items).moveSpeed;
    const canWalk =
      player.alive && player.hitstun <= 0 && player.grabbedBy === 0 && player.liftTicks <= 0 &&
      (player.action.kind === 'idle' || player.action.kind === 'attack');
    let moving = false;
    let yaw = view.root.rotation.y;
    if (target.active && this.input.targetId === 0 && canWalk && !this.paused) {
      const dx = target.x - pred.x;
      const dz = target.z - pred.z;
      const dist = Math.hypot(dx, dz);
      if (dist > 0.3) {
        const step = Math.min(dist, speed * dt);
        pred.x += (dx / dist) * step;
        pred.z += (dz / dist) * step;
        moving = true;
        yaw = Math.atan2(dx, dz);
        // nunca por detrás del servidor camino del destino (p. ej. con pocos FPS)
        if (Math.hypot(target.x - rx, target.z - rz) < Math.hypot(target.x - pred.x, target.z - pred.z)) {
          pred.x = rx;
          pred.z = rz;
        }
      }
    }
    if (!moving) {
      // sin orden propia: el servidor manda, se vuelve a su posición sin saltos
      const k = 1 - Math.exp(-10 * dt);
      pred.x += (rx - pred.x) * k;
      pred.z += (rz - pred.z) * k;
    }
    // nunca más adelantado que lo que el servidor recorre en el retraso actual
    const latency = (this.net?.ping ?? 150) / 1000 + (this.playback.delayTicks + 1) / TICK_RATE;
    const maxLead = speed * latency + 0.3;
    let ox = pred.x - rx;
    let oz = pred.z - rz;
    const lead = Math.hypot(ox, oz);
    if (lead > 4 || !player.alive) {
      // teletransporte, muerte, regreso a base: se adopta la del servidor
      pred.x = rx;
      pred.z = rz;
      ox = 0;
      oz = 0;
    } else if (lead > maxLead) {
      ox *= maxLead / lead;
      oz *= maxLead / lead;
      pred.x = rx + ox;
      pred.z = rz + oz;
    }
    view.setPrediction(ox, oz, moving, yaw);
  }

  /** Los snapshots se encolan; `updateNetPlayback` los reproduce con un pequeño retraso. */
  private onSnapshot(snapshot: WorldSnapshot): void {
    if (!this.welcome) return;
    this.playback.push(snapshot, performance.now());
  }

  /** Envía la entrada ya (desde el evento de teclado/ratón) en lugar de esperar al tick. */
  private sendInputNow(): void {
    if (!this.online || !this.net || !this.running || this.matchEnded || !this.sim.player) return;
    const input = this.input.sample();
    this.predictOwnCast(input);
    this.net.sendInput(input, true);
  }

  /**
   * Predicción del lanzamiento propio (online): si el servidor va a aceptar la
   * habilidad (`Simulation.wouldStartAbility`, mismas reglas), la animación y el
   * sonido empiezan ya; al llegar la confirmación siguen sin reiniciarse. Si no
   * llega a tiempo, se cancela. Daño, proyectiles y efectos siguen al servidor.
   */
  private predictOwnCast(input: InputState): void {
    const player = this.sim.player;
    if (!player) return;
    const view = this.views.get(player.id);
    if (!view || view.hasPredictedCast) return;
    const slot = (['q', 'w', 'e', 'r'] as const).find((s) => input.abilities[s]);
    if (!slot || !view.canPredictCast(slot) || !this.sim.wouldStartAbility(player, slot)) return;
    const duration = abilityDuration(getHeroDef(player.heroId).abilities[slot]);
    // ida y vuelta + margen de interpolación + colchón
    const windowMs = (this.net?.ping || 200) + ((this.playback.delayTicks + 2) * 1000) / TICK_RATE + 150;
    const yaw = Math.atan2(input.aimX - player.pos.x, input.aimZ - player.pos.z);
    view.predictCast(slot, duration, windowMs, yaw);
    this.sfx.predictCast(player, slot, duration, windowMs);
  }

  private resetNetPlayback(): void {
    this.playback.reset();
  }

  /**
   * Interpolación de snapshots (ver `SnapshotPlayback`): aplica el snapshot de
   * llegada, toma las posiciones del anterior como partida y devuelve el alpha.
   */
  private updateNetPlayback(dt: number): number {
    if (!this.welcome) return 1;
    const frame = this.playback.update(dt);
    if (!frame) return 1;
    if (frame.passed.length) {
      for (const s of frame.passed) for (const event of s.events) this.handleEvent(event);
      applySnapshot(this.sim, frame.to, this.welcome.heroId);
      if (frame.from !== frame.to) this.interpolateFrom(frame.from);
      const b = frame.to;
      if (b.match.ended) {
        const winner = b.match.winner ?? (b.match.score.blue >= b.match.score.red ? 'blue' : 'red');
        this.finishMatch(winner, b.match.score.blue, b.match.score.red);
      }
    }
    return frame.alpha;
  }

  /** Usa las posiciones del snapshot anterior como punto de partida de la interpolación. */
  private interpolateFrom(previous: WorldSnapshot): void {
    const heroes = new Map(previous.heroes.map((h) => [h.id, h]));
    for (const hero of this.sim.heroes) {
      const p = heroes.get(hero.id);
      // un enemigo en la niebla viaja sin posición
      if (!p || !hero.alive || p.hidden || p.x === undefined) continue;
      hero.prevPos.x = p.x;
      hero.prevPos.z = p.z;
    }
    const minions = new Map((previous.minions ?? []).map((m) => [m.id, m]));
    for (const minion of this.sim.minions) {
      const p = minions.get(minion.id);
      if (!p) continue;
      minion.prevPos.x = p.x;
      minion.prevPos.z = p.z;
    }
    const projectiles = new Map(previous.projectiles.map((p) => [p.id, p]));
    for (const projectile of this.sim.projectiles) {
      const p = projectiles.get(projectile.id);
      if (!p) continue;
      projectile.prevPos.x = p.x;
      projectile.prevPos.z = p.z;
    }
  }

  private onNetStatus(status: NetStatus, detail?: string): void {
    const labels: Record<NetStatus, string> = {
      idle: 'OFFLINE',
      connecting: 'CONNECTING',
      connected: 'ONLINE',
      error: 'ERROR',
      closed: 'OFFLINE',
    };
    this.hud.setNetStatus(labels[status], status);
    if (status === 'error') console.error(`[net] ${detail ?? 'unknown error'}`);
  }

  /** Abre/cierra la pausa. Offline detiene la simulación; online sólo bloquea la entrada. */
  setPaused(paused: boolean): void {
    if (this.paused === paused) return;
    this.paused = paused;
    this.input.clearPressed();
    this.input.clearMoveTarget();
    if (this.matchEnded) return;
    this.input.enabled = !paused;
    if (!this.online) {
      this.running = !paused;
      this.accumulator = 0;
    }
  }

  /** Rueda de emojis (T): online lo reparte el servidor; offline se muestra al momento. */
  sendEmote(emote: number): void {
    const player = this.sim.player;
    if (!this.canEmote || !player) return;
    if (this.online) {
      this.net?.sendEmote(emote);
      return;
    }
    const now = performance.now();
    if (now - this.lastEmoteAt < EMOTE_COOLDOWN_MS) return;
    this.lastEmoteAt = now;
    this.showEmote(player.id, emote);
  }

  private lastEmoteAt = -Infinity;

  /** Emoji sobre un héroe (llega del servidor online). */
  showEmote(heroId: number, emote: number): void {
    const emoji = EMOTES[emote];
    if (!emoji || !this.views.has(heroId)) return;
    this.vfx.emote(heroId, emoji);
  }

  /** Si la rueda de emojis puede abrirse (en juego, sin pausa ni fin de partida). */
  get canEmote(): boolean {
    return this.running && !this.paused && !this.matchEnded && Boolean(this.sim.player?.alive);
  }

  get isOnline(): boolean {
    return this.online;
  }

  get isMatchEnded(): boolean {
    return this.matchEnded;
  }

  /** Sensibilidad del ratón (0.25–2): avance de cámara hacia el cursor y paneo. */
  setMouseSensitivity(value: number): void {
    this.mouseSensitivity = THREE.MathUtils.clamp(value, 0.25, 3);
    this.cameraRig.setSensitivity(this.mouseSensitivity);
  }

  get isCameraLocked(): boolean {
    return this.cameraLocked;
  }

  private toggleCameraLock(): void {
    this.cameraLocked = !this.cameraLocked;
    this.cameraRig.setLocked(this.cameraLocked);
    this.hud.setCameraLocked(this.cameraLocked);
    this.vfx.announce(this.cameraLocked ? t('Cámara bloqueada') : t('Cámara libre'), this.cameraLocked ? '#9fd4ff' : '#ffd166');
  }

  private finishMatch(winner: 'blue' | 'red', blue: number, red: number): void {
    if (this.matchEnded) return;
    this.matchEnded = true;
    const player = this.sim.player;
    this.hud.showMatchEnd(
      winner,
      blue,
      red,
      this.online,
      player
        ? { team: player.team, heroId: player.heroId, level: player.level, kills: player.kills, deaths: player.deaths, seconds: this.sim.match.elapsedTicks / TICK_RATE }
        : undefined,
    );
    this.input.enabled = false;
    this.input.clearMoveTarget();
    this.input.clearPressed();
    if (!this.online) this.running = false;
  }

  private readonly onResize = (): void => {
    const width = window.innerWidth;
    const height = window.innerHeight;
    // antes volvía a 2× también en el móvil (que dibuja a 1,5× como máximo)
    this.basePixelRatio = Math.min(window.devicePixelRatio, this.touch ? 1.5 : 2);
    this.renderer.setPixelRatio(this.basePixelRatio * this.resolutionScale);
    this.renderer.setSize(width, height);
    this.cameraRig.resize(width, height);
  };

  /** Aplica los ajustes gráficos (Configuración → Gráficos). */
  setGraphics(settings: GraphicsSettings): void {
    this.graphics = { shadows: settings.shadows, fpsCap: settings.fpsCap, dynamicResolution: settings.dynamicResolution };
    this.arena?.setShadowQuality(settings.shadows);
    // sombras bajas: el mapa de sombras se redibuja a frames alternos
    this.renderer.shadowMap.autoUpdate = settings.shadows !== 'low';
    if (!settings.dynamicResolution && this.resolutionScale !== 1) this.setResolutionScale(1);
  }

  private setResolutionScale(scale: number): void {
    this.resolutionScale = scale;
    this.renderer.setPixelRatio(this.basePixelRatio * scale);
    this.renderer.setSize(window.innerWidth, window.innerHeight);
  }

  /**
   * Resolución dinámica: cada 2 s mira los FPS de partida; si caen por debajo
   * del objetivo baja la resolución interna un 15 % (hasta el 60 %) y, tras 6 s
   * holgados, la vuelve a subir.
   */
  private updateDynamicResolution(dt: number): void {
    if (!this.graphics.dynamicResolution || !this.running || this.paused) {
      this.dynResTime = 0;
      this.dynResFrames = 0;
      return;
    }
    this.dynResTime += dt;
    this.dynResFrames++;
    if (this.dynResTime < 2) return;
    const fps = this.dynResFrames / this.dynResTime;
    this.dynResTime = 0;
    this.dynResFrames = 0;
    const target = this.graphics.fpsCap > 0 ? this.graphics.fpsCap * 0.9 : 50;
    if (fps < target && this.resolutionScale > 0.6) {
      this.dynResCalm = 0;
      this.setResolutionScale(Math.max(0.6, Math.round((this.resolutionScale - 0.15) * 100) / 100));
    } else if (fps > target + 8 || (this.graphics.fpsCap > 0 && fps >= this.graphics.fpsCap * 0.97)) {
      this.dynResCalm += 2;
      if (this.dynResCalm >= 6 && this.resolutionScale < 1) {
        this.dynResCalm = 0;
        this.setResolutionScale(Math.min(1, Math.round((this.resolutionScale + 0.1) * 100) / 100));
      }
    } else {
      this.dynResCalm = 0;
    }
  }

  /** ¿El punto (x, z) del suelo cae en pantalla (con `VIEW_MARGIN` de holgura)? */
  private inView(x: number, z: number, height = 1): boolean {
    const camera = this.cameraRig.camera;
    this.ndc.set(x, height, z).project(camera);
    const mx = 1 + VIEW_MARGIN / Math.max(1, camera.right);
    const my = 1 + VIEW_MARGIN / Math.max(1, camera.top);
    return Math.abs(this.ndc.x) <= mx && Math.abs(this.ndc.y) <= my;
  }

  /**
   * Qué se dibuja: los enemigos en la niebla nunca, y ninguna unidad fuera de
   * cámara (las mallas animadas no usan el recorte automático de Three.js porque
   * su caja no sigue a la animación; aquí se recorta por la posición).
   */
  private updateVisibility(player: HeroState): void {
    const sources = this.fogSources;
    for (const hero of this.sim.heroes) {
      const view = this.views.get(hero.id);
      if (!view) continue;
      const fogged = hero.team !== player.team && hero.alive && (Boolean(hero.hidden) || (sources !== null && !isVisible(sources, hero.pos.x, hero.pos.z, hero.radius)));
      view.root.visible = !fogged && (hero.id === player.id || this.inView(view.root.position.x, view.root.position.z));
    }
    for (const minion of this.sim.minions) {
      const view = this.minionViews.get(minion.id);
      if (!view) continue;
      const fogged = minion.team !== player.team && (Boolean(minion.hidden) || (sources !== null && !isVisible(sources, minion.pos.x, minion.pos.z, minion.radius)));
      view.root.visible = !fogged && this.inView(view.root.position.x, view.root.position.z);
    }
    for (const view of this.structureViews.values()) {
      view.root.visible = this.inView(view.root.position.x, view.root.position.z, 2);
    }
  }

  private readonly onWheel = (event: WheelEvent): void => {
    event.preventDefault();
    this.cameraRig.zoom(event.deltaY);
  };

  private rangeKeyHeld = false;
  private rangeIndicator?: { group: THREE.Group; radius: number };

  private readonly onKeyUp = (event: KeyboardEvent): void => {
    if (event.code === 'KeyA') this.rangeKeyHeld = false;
  };

  private readonly onKeyDown = (event: KeyboardEvent): void => {
    if (event.code === 'KeyM' && !event.repeat && !event.ctrlKey && !event.metaKey && !event.shiftKey && !event.altKey) {
      const target = event.target as HTMLElement | null;
      if (target && (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA')) return;
      const muted = this.sfx.toggleMute();
      if (this.running) this.vfx.announce(muted ? t('Sonido desactivado') : t('Sonido activado'), muted ? '#ff9a8a' : '#9fe0b0');
      return;
    }
    if (event.code === 'KeyY' && !event.repeat && !event.ctrlKey && !event.metaKey && !event.shiftKey) {
      if (this.running && !this.paused && !this.matchEnded) {
        event.preventDefault();
        this.toggleCameraLock();
      }
      return;
    }
    if (event.code === 'KeyA' && !event.ctrlKey && !event.metaKey) {
      this.rangeKeyHeld = true;
      return;
    }
    if (event.code === 'KeyI' && !event.repeat && !event.ctrlKey && !event.shiftKey && this.running) {
      event.preventDefault();
      this.hud.setHeroBoardOpen(false);
      this.shop?.toggle();
      return;
    }
    if (event.code === 'KeyO' && !event.repeat && !event.ctrlKey && !event.metaKey && !event.shiftKey && this.running) {
      event.preventDefault();
      this.shop?.setOpen(false);
      this.hud.toggleHeroBoard();
      return;
    }
    if (event.code === 'Escape' && this.hud.isHeroBoardOpen) {
      this.hud.setHeroBoardOpen(false);
      return;
    }
    if (event.code === 'Escape' && this.shop?.isOpen) {
      this.shop.setOpen(false);
      return;
    }
    if (event.code === 'F3') {
      event.preventDefault();
      this.hud.toggleDebug();
    }
  };
}

/** Material de profundidad propio para las sombras de las mallas con esqueleto. */
const SKINNED_SHADOW_DEPTH = new THREE.MeshDepthMaterial();

/**
 * three usa un único material de profundidad para todas las sombras: al alternar
 * entre mallas con esqueleto (héroes, estatuas) y estáticas (minions, props)
 * recalcula el programa en cada cambio (~1000 veces por segundo). Con un
 * material aparte para las mallas con esqueleto, cada uno conserva su programa.
 */
function useSkinnedShadowDepth(root: THREE.Object3D): void {
  root.traverse((object) => {
    if ((object as THREE.SkinnedMesh).isSkinnedMesh) object.customDepthMaterial = SKINNED_SHADOW_DEPTH;
  });
}
